import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, Outlet } from "react-router-dom";
import { Avatar } from "./avatar";
import {
  Dropdown,
  DropdownButton,
  //DropdownDivider,
  DropdownItem,
  DropdownLabel,
  DropdownMenu,
} from "./dropdown";
import { Navbar, NavbarItem, NavbarSection, NavbarSpacer } from "./navbar";
import {
  Sidebar,
  SidebarBody,
  SidebarFooter,
  SidebarHeader,
  SidebarItem,
  SidebarLabel,
  SidebarSection,
  SidebarSpacer,
} from "./sidebar";
import { SidebarLayout } from "./sidebar-layout";
import { esInversionista } from "../utils/roles";
import { useAuth } from "./AuthProvider";
import { VenueProvider, useVenue } from "./VenueProvider";
import {
  Combobox,
  ComboboxButton,
  ComboboxInput,
  ComboboxOption,
  ComboboxOptions,
} from "@headlessui/react";
import {
  ArrowRightStartOnRectangleIcon,
  ChevronUpDownIcon,
  ChevronUpIcon,
  Cog8ToothIcon,
  ShieldCheckIcon,
  //LightBulbIcon,
  UserIcon,
  HomeIcon,
  ClipboardDocumentCheckIcon,
  RectangleStackIcon,
  QuestionMarkCircleIcon,
  SparklesIcon,
  CalendarDaysIcon,
  UsersIcon,
  BuildingOffice2Icon,
  BuildingOfficeIcon,
  AdjustmentsHorizontalIcon,
  LightBulbIcon,
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  ClipboardDocumentListIcon,
  NewspaperIcon,
  ChartBarIcon,
  TagIcon,
  BriefcaseIcon,
  Squares2X2Icon,
  MegaphoneIcon,
  EyeSlashIcon,
  EyeIcon,
  MapPinIcon,
  BanknotesIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/20/solid";
import { signOut } from "firebase/auth";
import { collection, onSnapshot, query, where } from "firebase/firestore";
import { auth, db } from "../firebase";
import clsx from "clsx";
import { getUnseenCount } from "./Changelog";
import { usePresence } from "../hooks/usePresence";
import { useDemoMode } from "../hooks/useDemoMode";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  arrayMove,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Bars2Icon,
  PencilSquareIcon,
  CheckIcon,
  ArrowUturnLeftIcon,
} from "@heroicons/react/20/solid";
import {
  useSidebarOrder,
  applyVisibleOrder,
} from "../hooks/useSidebarOrder";

// Sección principal del sidebar como configuración, para poder reordenarla.
// Los `id` son estables y se persisten en users/{uid}.sidebarOrder — el label
// puede cambiar ("recursos" se muestra como "Servicios"), el id jamás.
const MAIN_NAV = [
  { id: "inicio", label: "Inicio", Icon: HomeIcon, path: "/dashboard/home" },
  { id: "calendario", label: "Calendario", Icon: CalendarDaysIcon, path: "/dashboard/calendario" },
  { id: "leads", label: "Leads", Icon: LightBulbIcon, path: "/dashboard/leads" },
  // El id "proveedores" se conserva (users/{uid}.sidebarOrder); la sección
  // es Directorio y también ilumina /proveedores/:id y /clientes/:id.
  { id: "proveedores", label: "Directorio", Icon: BriefcaseIcon, path: "/dashboard/directorio", prefix: true, hideForViewer: true, alsoPrefixes: ["/dashboard/proveedores", "/dashboard/clientes"] },
  // El id "recursos" se conserva: vive persistido en users/{uid}.sidebarOrder.
  { id: "recursos", label: "Servicios", Icon: RectangleStackIcon, path: "/dashboard/servicios", prefix: true, hideForViewer: true },
  { id: "eventos", label: "Eventos", Icon: ClipboardDocumentCheckIcon, path: "/dashboard/eventos", prefix: true },
  { id: "cuentas", label: "Cuentas", Icon: CurrencyDollarIcon, path: "/dashboard/cuentas", prefix: true, hideForViewer: true },
  { id: "reportes", label: "Reportes", Icon: ChartBarIcon, path: "/dashboard/reportes", prefix: true, hideForViewer: true },
  { id: "bitacora", label: "Bitácora", Icon: ClipboardDocumentListIcon, path: "/dashboard/bitacora", hideForViewer: true, hideForInversionista: true },
  { id: "config", label: "Configuración", Icon: Cog8ToothIcon, path: "/dashboard/config", hideForViewer: true, hideForInversionista: true },
];

// Item de la sección principal, arrastrable solo en modo edición. En edición
// el click no navega (todo el item es el handle — el modo explícito evita
// las navegaciones accidentales de un drag fallido).
function SortableNavItem({ item, current, isCollapsed, editing, onNavigate }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !editing });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        // En edición el navegador no debe interpretar el gesto como scroll;
        // fuera de edición el drawer scrollea normal.
        touchAction: editing ? "none" : undefined,
      }}
      className={clsx(
        isDragging && "relative z-10 opacity-80",
        // El botón de SidebarItem trae cursor-default; en edición lo pisa
        // el cursor de agarre (selector descendiente + !important).
        editing && "[&_button]:!cursor-grab [&_button]:active:!cursor-grabbing"
      )}
      {...attributes}
      {...listeners}
    >
      <SidebarItem
        onClick={editing ? undefined : onNavigate}
        current={current}
        isCollapsed={isCollapsed}
      >
        <item.Icon className="h-6 w-6" />
        <SidebarLabel isCollapsed={isCollapsed}>{item.label}</SidebarLabel>
        {editing && !isCollapsed && (
          <Bars2Icon className="ml-auto h-4 w-4 shrink-0 !fill-zinc-400" />
        )}
      </SidebarItem>
    </div>
  );
}

const SidebarItems = React.memo(({ isCollapsed }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { userInfo } = useAuth();
  const isAdmin = userInfo?.role === "admin";
  const isViewer = userInfo?.role === "viewer";
  const isInversionista = esInversionista(userInfo);
  const [demoMode, setDemoMode] = useDemoMode();
  const showAdmin = isAdmin && !demoMode;
  const [pendingFeedbackCount, setPendingFeedbackCount] = useState(0);

  // Admin-only: subscribe to pending feedback so the sidebar shows a dot
  // when something new lands. Mirrors the Novedades indicator pattern.
  useEffect(() => {
    if (!isAdmin) return undefined;
    const q = query(
      collection(db, "feedback"),
      where("status", "==", "pending")
    );
    const unsub = onSnapshot(
      q,
      (snap) => setPendingFeedbackCount(snap.size),
      () => setPendingFeedbackCount(0)
    );
    return () => unsub();
  }, [isAdmin]);

  // Orden personalizable de la sección principal (users/{uid}.sidebarOrder).
  // El reorder solo se activa en modo edición y con el sidebar expandido.
  const { order, save, reset, isCustomized } = useSidebarOrder();
  const [editingOrder, setEditingOrder] = useState(false);
  // Mouse y touch por separado: PointerSensor con `distance` pelea con el
  // scroll táctil (el drawer scrollea en vez de arrastrar, o al revés). En
  // touch, mantener presionado ~250ms inicia el drag; un swipe normal sigue
  // scrolleando. Complementa el touch-action del item en modo edición.
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 6 },
    }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const itemsById = React.useMemo(
    () => Object.fromEntries(MAIN_NAV.map((it) => [it.id, it])),
    []
  );
  const visibleItems = order
    .map((id) => itemsById[id])
    .filter(Boolean)
    .filter((it) => !(it.hideForViewer && isViewer))
    .filter((it) => !(it.hideForInversionista && isInversionista));

  const handleDragEnd = ({ active, over }) => {
    if (!over || active.id === over.id) return;
    const visibleIds = visibleItems.map((it) => it.id);
    const reordered = arrayMove(
      visibleIds,
      visibleIds.indexOf(active.id),
      visibleIds.indexOf(over.id)
    );
    // Los ids que este rol no ve conservan su posición en el orden completo.
    save(applyVisibleOrder(order, reordered));
  };

  const isCurrent = (item) =>
    item.prefix
      ? location.pathname.startsWith(item.path) ||
        (item.alsoPrefixes || []).some((p) => location.pathname.startsWith(p))
      : location.pathname === item.path;

  // La ficha del venue se mudó a Configuración → Venue (para el owner es
  // configuración, no una sección diaria). La lista multi-venue sigue viva
  // para admins, en su propia sección.
  const targetPath = (item) => item.path;

  return (
    <>
      <SidebarSection isCollapsed={isCollapsed}>
        {!isCollapsed && (
          <div className="mb-1 flex items-center justify-end gap-2 px-2 text-xs/6 font-medium text-zinc-500 dark:text-zinc-400">
            <span className="mr-auto">Menú</span>
            {editingOrder && isCustomized && (
              <button
                type="button"
                onClick={reset}
                className="flex items-center gap-1 rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                title="Restablecer el orden original"
                aria-label="Restablecer el orden original"
              >
                <ArrowUturnLeftIcon className="h-4 w-4" />
              </button>
            )}
            <button
              type="button"
              onClick={() => setEditingOrder((v) => !v)}
              className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
              title={editingOrder ? "Terminar de ordenar" : "Ordenar el menú"}
              aria-label={editingOrder ? "Terminar de ordenar" : "Ordenar el menú"}
            >
              {editingOrder ? (
                <CheckIcon className="h-4 w-4" />
              ) : (
                <PencilSquareIcon className="h-4 w-4" />
              )}
            </button>
          </div>
        )}
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <SortableContext
            items={visibleItems.map((it) => it.id)}
            strategy={verticalListSortingStrategy}
          >
            {visibleItems.map((item) => (
              <SortableNavItem
                key={item.id}
                item={item}
                current={isCurrent(item)}
                isCollapsed={isCollapsed}
                editing={editingOrder && !isCollapsed}
                onNavigate={() => navigate(targetPath(item))}
              />
            ))}
          </SortableContext>
        </DndContext>
      </SidebarSection>

      {isAdmin && (
        <SidebarSection isCollapsed={isCollapsed}>
          {isCollapsed ? (
            // Collapsed: show only the icon toggle. Without this, turning
            // demoMode on while collapsed would hide the whole admin section
            // with no way to re-enable it short of expanding the sidebar.
            <div className="mb-1 flex justify-center">
              <button
                type="button"
                onClick={() => setDemoMode(!demoMode)}
                className="rounded p-1 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                title={demoMode ? "Mostrar sección de administración" : "Ocultar sección de administración"}
                aria-label={demoMode ? "Mostrar sección de administración" : "Ocultar sección de administración"}
              >
                {demoMode ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          ) : (
            <div className="mb-1 flex items-center justify-end gap-2 px-2 text-xs/6 font-medium text-zinc-500 dark:text-zinc-400">
              <span className="mr-auto">Administración</span>
              <button
                type="button"
                onClick={() => setDemoMode(!demoMode)}
                className="rounded p-0.5 text-zinc-400 hover:bg-zinc-100 hover:text-zinc-700 dark:hover:bg-zinc-800 dark:hover:text-zinc-200"
                title={demoMode ? "Mostrar sección de administración" : "Ocultar sección de administración"}
                aria-label={demoMode ? "Mostrar sección de administración" : "Ocultar sección de administración"}
              >
                {demoMode ? (
                  <EyeSlashIcon className="h-4 w-4" />
                ) : (
                  <EyeIcon className="h-4 w-4" />
                )}
              </button>
            </div>
          )}
          {showAdmin && (
            <>
          <SidebarItem
            onClick={() => navigate("/dashboard/admin")}
            current={location.pathname === "/dashboard/admin"}
            isCollapsed={isCollapsed}
          >
            <BuildingOffice2Icon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>Panel Admin</SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/venues")}
            current={location.pathname.startsWith("/dashboard/venues")}
            isCollapsed={isCollapsed}
          >
            <BuildingOfficeIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>Venues</SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/reportes-admin")}
            current={location.pathname === "/dashboard/reportes-admin"}
            isCollapsed={isCollapsed}
          >
            <ShieldCheckIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>Reportes admin</SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/admin-telegram")}
            current={location.pathname === "/dashboard/admin-telegram"}
            isCollapsed={isCollapsed}
          >
            <PaperAirplaneIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>Bot Telegram</SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/venue-pagos")}
            current={location.pathname === "/dashboard/venue-pagos"}
            isCollapsed={isCollapsed}
          >
            <BanknotesIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>Pagos Venues</SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/users")}
            current={location.pathname === "/dashboard/users"}
            isCollapsed={isCollapsed}
          >
            <UsersIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>Usuarios</SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/venue-requests")}
            current={location.pathname === "/dashboard/venue-requests"}
            isCollapsed={isCollapsed}
          >
            <BuildingOffice2Icon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>
              Solicitudes de Venues
            </SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/feedback")}
            current={location.pathname === "/dashboard/feedback"}
            isCollapsed={isCollapsed}
          >
            <ChatBubbleLeftRightIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>Feedback</SidebarLabel>
            {pendingFeedbackCount > 0 && !isCollapsed && (
              <span className="ml-auto inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[#1b4d2e] px-1.5 text-[11px] font-semibold text-white">
                {pendingFeedbackCount > 99 ? "99+" : pendingFeedbackCount}
              </span>
            )}
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/open-data")}
            current={location.pathname === "/dashboard/open-data"}
            isCollapsed={isCollapsed}
          >
            <AdjustmentsHorizontalIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>
              Datos del Sistema
            </SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/catalogos")}
            current={location.pathname === "/dashboard/catalogos"}
            isCollapsed={isCollapsed}
          >
            <Squares2X2Icon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>
              Catálogos de Venue
            </SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/tipos-evento")}
            current={location.pathname === "/dashboard/tipos-evento"}
            isCollapsed={isCollapsed}
          >
            <TagIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>
              Tipos de Evento
            </SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/ubicaciones")}
            current={location.pathname === "/dashboard/ubicaciones"}
            isCollapsed={isCollapsed}
          >
            <MapPinIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>
              Ubicaciones
            </SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/temas-destino")}
            current={location.pathname === "/dashboard/temas-destino"}
            isCollapsed={isCollapsed}
          >
            <SparklesIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>
              Temas Destino
            </SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/proveedor-categorias")}
            current={location.pathname === "/dashboard/proveedor-categorias"}
            isCollapsed={isCollapsed}
          >
            <TagIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>
              Categorías Proveedores
            </SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/blog")}
            current={location.pathname.startsWith("/dashboard/blog")}
            isCollapsed={isCollapsed}
          >
            <NewspaperIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>Blog</SidebarLabel>
          </SidebarItem>

          <SidebarItem
            onClick={() => navigate("/dashboard/marketing/campaigns")}
            current={location.pathname.startsWith("/dashboard/marketing")}
            isCollapsed={isCollapsed}
          >
            <MegaphoneIcon className="h-6 w-6" />
            <SidebarLabel isCollapsed={isCollapsed}>Marketing</SidebarLabel>
          </SidebarItem>
            </>
          )}
        </SidebarSection>
      )}
    </>
  );
});

const VenueSelector = ({ isCollapsed }) => {
  const { venues, selectedVenueId, setSelectedVenueId, venueLoading } = useVenue();
  const [query, setQuery] = useState("");
  const { userInfo } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = userInfo?.role === "admin";

  // Para un owner parado en la ficha de un venue (una ruta vieja: hoy entra
  // por Configuración → Venue, que rebindea sola), cambiar el selector debe
  // llevarlo a la ficha del venue nuevo. El admin se excluye: suele abrir
  // desde la lista un venue distinto al seleccionado, y saltarle la página
  // sería sorpresa.
  const handleSelect = (id) => {
    if (!id) return;
    setSelectedVenueId(id);
    if (!isAdmin && location.pathname.startsWith("/dashboard/venues/")) {
      navigate(`/dashboard/venues/${id}`);
    }
  };

  if (venueLoading || venues.length === 0) return null;

  // Single venue: static label
  if (venues.length === 1) {
    const venue = venues[0];
    return (
      <div className="border-b border-zinc-200 dark:border-zinc-700 px-3 py-3">
        {isCollapsed ? (
          <div
            className="mx-auto text-center text-xs font-bold"
            style={{ color: "#1b4d2e" }}
            title={venue.name || venue.id}
          >
            {(venue.name || venue.id).length > 5
              ? (venue.name || venue.id).slice(0, 5) + "..."
              : venue.name || venue.id}
          </div>
        ) : (
          <div
            className="truncate text-center text-sm font-semibold"
            style={{ color: "#1b4d2e" }}
          >
            {venue.name || venue.id}
          </div>
        )}
      </div>
    );
  }

  // Multiple venues: dropdown when expanded, initial badge when collapsed
  if (isCollapsed) {
    const selected = venues.find((v) => v.id === selectedVenueId);
    return (
      <div className="border-b border-zinc-200 dark:border-zinc-700 px-3 py-3">
        <div
          className="mx-auto text-center text-xs font-bold cursor-default"
          style={{ color: "#1b4d2e" }}
          title={selected?.name || selected?.id || ""}
        >
          {((selected?.name || selected?.id || "?").length > 5
            ? (selected?.name || selected?.id || "?").slice(0, 5) + "..."
            : selected?.name || selected?.id || "?")}
        </div>
      </div>
    );
  }

  const filteredVenues =
    query === ""
      ? venues
      : venues.filter((v) =>
          (v.name || v.id).toLowerCase().includes(query.toLowerCase())
        );

  return (
    <div className="border-b border-zinc-200 dark:border-zinc-700 px-3 py-3">
      <Combobox
        value={selectedVenueId}
        onChange={(id) => {
          handleSelect(id);
          setQuery("");
        }}
        onClose={() => setQuery("")}
      >
        <div className="relative">
          <ComboboxInput
            aria-label="Venue"
            displayValue={(id) => {
              const v = venues.find((x) => x.id === id);
              return v ? v.name || v.id : "";
            }}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={(e) => e.target.select()}
            placeholder="Buscar venue..."
            className={clsx(
              "block w-full rounded-lg border border-zinc-950/10 bg-white py-1.5 pl-3 pr-8 text-sm text-zinc-950 shadow-sm",
              "focus:outline-none focus:ring-2 focus:ring-blue-500",
              "dark:border-white/10 dark:bg-white/5 dark:text-white"
            )}
          />
          <ComboboxButton className="absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon
              className="size-4 text-zinc-500 dark:text-zinc-400"
              aria-hidden="true"
            />
          </ComboboxButton>
        </div>
        <ComboboxOptions
          anchor="bottom start"
          className={clsx(
            "z-50 mt-1 max-h-72 w-[var(--input-width)] min-w-48 overflow-y-auto rounded-xl p-1",
            "bg-white/95 shadow-lg ring-1 ring-zinc-950/10 backdrop-blur-xl",
            "dark:bg-zinc-800/95 dark:ring-inset dark:ring-white/10",
            "transition-opacity duration-100 ease-in data-[transition]:pointer-events-none data-[closed]:data-[leave]:opacity-0",
            "empty:invisible focus:outline-none"
          )}
        >
          {filteredVenues.length === 0 ? (
            <div className="px-3 py-2 text-sm text-zinc-500 dark:text-zinc-400">
              Sin resultados
            </div>
          ) : (
            filteredVenues.map((venue) => (
              <ComboboxOption
                key={venue.id}
                value={venue.id}
                className={clsx(
                  "cursor-default select-none truncate rounded-lg px-3 py-1.5 text-sm",
                  "text-zinc-950 dark:text-white",
                  "data-[focus]:bg-blue-500 data-[focus]:text-white",
                  "data-[selected]:font-semibold"
                )}
              >
                {venue.name || venue.id}
              </ComboboxOption>
            ))
          )}
        </ComboboxOptions>
      </Combobox>
    </div>
  );
};

const SinVenueAsignado = ({ email, onLogout }) => (
  <div className="flex min-h-dvh items-center justify-center bg-zinc-50 px-4">
    <div className="w-full max-w-md rounded-2xl border border-zinc-200 bg-white p-8 text-center shadow-sm">
      <BuildingOffice2Icon className="mx-auto h-10 w-10 text-zinc-300" />
      <h1 className="mt-4 text-lg font-semibold text-zinc-900">
        Tu cuenta aún no tiene un venue asignado
      </h1>
      <p className="mt-2 text-sm text-zinc-600">
        Para usar el panel, Venueboard debe vincular tu cuenta
        {email ? <> (<span className="font-medium">{email}</span>)</> : null} con
        el venue en el que vas a trabajar. Escríbenos a{" "}
        <a href="mailto:hola@venueboard.com" className="font-medium underline">
          hola@venueboard.com
        </a>{" "}
        y en cuanto quede asignado podrás entrar.
      </p>
      <button
        type="button"
        onClick={onLogout}
        className="mt-6 inline-flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90"
        style={{ backgroundColor: "#1b4d2e" }}
      >
        <ArrowRightStartOnRectangleIcon className="h-4 w-4" />
        Cerrar sesión
      </button>
    </div>
  </div>
);

// Aviso fijo para el inversionista (P-FRA-13). Si algún botón de escritura
// se escapó en la UI, la regla lo rechaza y este aviso le da contexto.
const BannerSoloLectura = ({ fullBleed }) => (
  <div
    className={clsx(
      "flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm text-zinc-700",
      fullBleed ? "mb-3" : "mx-6 mt-6 -mb-2"
    )}
  >
    <EyeIcon className="h-4 w-4 shrink-0 text-zinc-500" />
    <span>
      <span className="font-medium">Acceso de solo lectura.</span> Puedes
      consultar toda la información del venue, pero no modificarla.
    </span>
  </div>
);

const DashboardContent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userInfo, authLoading, infoLoading } = useAuth();
  const { selectedVenueId, venues, venueLoading } = useVenue();
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [hasNewChangelog, setHasNewChangelog] = useState(() => getUnseenCount() > 0);

  usePresence({ user, userInfo, venueId: selectedVenueId });

  // Re-check when navigating away from changelog (user just saw it)
  useEffect(() => {
    if (location.pathname !== "/dashboard/changelog") {
      const timer = setTimeout(() => setHasNewChangelog(getUnseenCount() > 0), 2000);
      return () => clearTimeout(timer);
    }
  }, [location.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  if (authLoading || infoLoading) {
    return <div>Loading...</div>;
  }

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  const handleCollapseChange = () => {
    setIsCollapsed(!isCollapsed);
  };

  // Cuenta del panel (owner, viewer, inversionista) sin ningún venue que
  // pueda abrir: sin venues en su array, o con ids de venues que ya no
  // existen. Antes entraba a un panel en blanco; ahora se le dice qué pasa.
  // El admin ve todos los venues y nunca cae aquí.
  if (userInfo && userInfo.role !== "admin" && !venueLoading && venues.length === 0) {
    return <SinVenueAsignado email={userInfo?.email} onLogout={handleLogout} />;
  }

  // Páginas que manejan su propio ancho y se quedan con la tarjeta completa:
  // el tablero de leads (columnas que scrollean a lo ancho) y Reportes
  // (índice lateral propio + tablas anchas). Coincidencia EXACTA a propósito
  // — el detalle de un lead sí quiere los márgenes normales.
  const FULL_BLEED_PATHS = ["/dashboard/leads", "/dashboard/reportes"];
  const pathActual = location.pathname.replace(/\/$/, "");
  const isFullBleed = FULL_BLEED_PATHS.includes(pathActual);

  return (
    <SidebarLayout
      navbar={
        <Navbar>
          <NavbarSpacer />
          <NavbarSection>
            <Dropdown>
              <DropdownButton as={NavbarItem}>
                <Avatar
                  slot="icon"
                  initials={userInfo?.name?.charAt(0) || "--"}
                  className="bg-gray-500 text-white"
                />
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="bottom end">
                <DropdownItem as="button" onClick={() => navigate("/dashboard/perfil")}>
                  <UserIcon className="h-5 w-5" />
                  <DropdownLabel>Mi Perfil</DropdownLabel>
                </DropdownItem>
                <DropdownItem as="button" onClick={handleLogout}>
                  <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                  <DropdownLabel>Cerrar sesión</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </NavbarSection>
        </Navbar>
      }
      sidebar={
        <Sidebar
          isCollapsed={isCollapsed}
          onCollapseChange={handleCollapseChange}
        >
          <SidebarHeader
            isCollapsed={isCollapsed}
            onCollapseChange={handleCollapseChange}
          >
            {/* Lockup horizontal (icono + wordmark), el mismo del navbar del
                homesite. El logo apilado (logoH2) a lo ancho del sidebar se
                comía ~150px de alto. */}
            {isCollapsed ? (
              <img src="/icon.png" alt="Venueboard" className="mx-auto h-8 w-auto" />
            ) : (
              <div className="flex items-center justify-center gap-2.5 py-1">
                <img src="/icon.png" alt="" className="h-9 w-auto" />
                <img src="/text.png" alt="Venueboard" className="w-40" />
              </div>
            )}
          </SidebarHeader>
          <VenueSelector isCollapsed={isCollapsed} />
          <SidebarBody isCollapsed={isCollapsed}>
            <SidebarItems isCollapsed={isCollapsed} />
            <SidebarSpacer isCollapsed={isCollapsed} />
            <SidebarSection isCollapsed={isCollapsed}>
              <SidebarItem
                onClick={() => navigate("/dashboard/ayuda")}
                current={location.pathname === "/dashboard/ayuda"}
                isCollapsed={isCollapsed}
              >
                <QuestionMarkCircleIcon className="h-6 w-6" />
                <SidebarLabel isCollapsed={isCollapsed}>Ayuda</SidebarLabel>
              </SidebarItem>
              <SidebarItem
                onClick={() => {
                  navigate("/dashboard/changelog");
                  setHasNewChangelog(false);
                }}
                current={location.pathname === "/dashboard/changelog"}
                isCollapsed={isCollapsed}
              >
                <SparklesIcon className="h-6 w-6" />
                <SidebarLabel isCollapsed={isCollapsed}>Novedades</SidebarLabel>
                {hasNewChangelog && !isCollapsed && (
                  <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-[#1b4d2e] data-[current]:bg-white" />
                )}
              </SidebarItem>
            </SidebarSection>
          </SidebarBody>
          <SidebarFooter className="max-lg:hidden" isCollapsed={isCollapsed}>
            <Dropdown>
              <DropdownButton as={SidebarItem}>
                <span className="flex min-w-0 items-center gap-3">
                  <Avatar
                    slot="icon"
                    initials={userInfo?.name?.charAt(0) || "--"}
                    className="size-10 bg-[#1b4d2e] text-white"
                    square
                  />
                  <span
                    className={clsx("min-w-0", isCollapsed ? "hidden" : "")}
                  >
                    <span className="block truncate text-sm font-medium text-gray-800">
                      {userInfo ? userInfo.name : "User"}
                    </span>
                    <span className="block truncate text-xs font-normal text-gray-500">
                      {userInfo ? userInfo.email : "email"}
                    </span>
                  </span>
                </span>
                <ChevronUpIcon
                  className={clsx("h-5 w-5", isCollapsed ? "hidden" : "")}
                />
              </DropdownButton>
              <DropdownMenu className="min-w-64" anchor="top start">
                <DropdownItem as="button" onClick={() => navigate("/dashboard/perfil")}>
                  <UserIcon className="h-5 w-5" />
                  <DropdownLabel>Mi Perfil</DropdownLabel>
                </DropdownItem>
                <DropdownItem as="button" onClick={handleLogout}>
                  <ArrowRightStartOnRectangleIcon className="h-5 w-5" />
                  <DropdownLabel>Cerrar sesión</DropdownLabel>
                </DropdownItem>
              </DropdownMenu>
            </Dropdown>
          </SidebarFooter>
        </Sidebar>
      }
      isCollapsed={isCollapsed}
      onCollapseChange={handleCollapseChange}
      fullBleed={isFullBleed}
    >
      {esInversionista(userInfo) && <BannerSoloLectura fullBleed={isFullBleed} />}
      <div className={isFullBleed ? undefined : "p-6"}>
        <Outlet />
      </div>
    </SidebarLayout>
  );
};

const Dashboard = () => (
  <VenueProvider>
    <DashboardContent />
  </VenueProvider>
);

export default Dashboard;
